/**
 * Katalog: produkter + kollektioner fra edge fn "shopify-products" /
 * "shopify-collections" (cachet katalog — ingen direkte Shopify-kald).
 */
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase/client";
import { callFunction } from "@/lib/functions";
import type {
  ShopifyCollection,
  ShopifyCollectionsResponse,
  ShopifyProduct,
  ShopifyProductsResponse,
} from "../types";

const FIVE_MINUTES = 5 * 60 * 1000;

export function useProducts() {
  return useQuery({
    queryKey: ["produkter"],
    queryFn: async (): Promise<ShopifyProduct[]> => {
      const data = await callFunction<ShopifyProductsResponse>("shopify-products");
      return data.products ?? [];
    },
    staleTime: FIVE_MINUTES,
    retry: 2,
  });
}

/** Ét produkt via handle (genbruger produkt-cachen). */
export function useProduct(handle: string | undefined) {
  const query = useProducts();
  const product = useMemo(
    () => (handle ? query.data?.find((p) => p.handle === handle) ?? null : null),
    [query.data, handle],
  );
  return { ...query, product };
}

function useCollectionImageOverrides() {
  return useQuery({
    queryKey: ["kollektionsbilleder"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("collection_image_overrides")
        .select("collection_id, image_url");
      if (error) throw error;
      return data ?? [];
    },
    staleTime: FIVE_MINUTES,
  });
}

export function useCollections() {
  const raw = useQuery({
    queryKey: ["kollektioner"],
    queryFn: async (): Promise<ShopifyCollection[]> => {
      const data = await callFunction<ShopifyCollectionsResponse>("shopify-collections");
      return data.collections ?? [];
    },
    staleTime: FIVE_MINUTES,
    retry: 2,
  });
  const { data: overrides } = useCollectionImageOverrides();

  const data = useMemo(() => {
    if (!raw.data) return undefined;
    return raw.data.map((c) => {
      const override = overrides?.find((o) => o.collection_id === c.id);
      return override
        ? { ...c, image: { src: override.image_url, alt: c.title }, hasOverride: true }
        : { ...c, hasOverride: false };
    });
  }, [raw.data, overrides]);

  return { ...raw, data };
}

/** Map: productId → kollektions-id'er (til COLLECTION-rabat og relaterede produkter). */
export function useProductCollectionMap(): Map<string, string[]> {
  const { data: collections } = useCollections();
  return useMemo(() => {
    const map = new Map<string, string[]>();
    for (const c of collections ?? []) {
      for (const pid of c.productIds) {
        const list = map.get(pid);
        if (list) list.push(c.id);
        else map.set(pid, [c.id]);
      }
    }
    return map;
  }, [collections]);
}
